/* eslint-disable new-cap */
/* eslint-disable camelcase */
import { seedUserRead } from "../../../services/database/user-read-cache";
import { liveCalc, LiveCalc } from "../calc-result-read";
import { buildCalcResult, CalcFigures, valuesFromParams } from "../calc-result";
import {
  opsMissionTypeEnum,
  parameterStruct,
  processResultStateEnum,
  property,
  targetPlatformEnum,
} from "../dataStruct";
import {
  addParameter,
  alignMarkdownTablesInDocument,
  currentSignatureId,
  deleteAllParameters,
  execAccuParameters,
  queueCommand,
  getParameter,
  getParameterOfArray,
  getParametersArray,
  getUtcTime,
  isBenignLwwReject,
  queueParameter,
  markDownProcessing,
  padLeft,
} from "../functions";
import { navLegStruct } from "./navLogReport";
import { rowIdFor } from "../../../db/rowid";
import { aircraftStruct } from "./providers/fppProvider";
import { opsAocRulesProvider, rmksStruct } from "./providers/opsAocRulesProvider";

/** Reads the caller started together with its own, to be awaited by initFuel. */
export interface StartedFuelReads {
  fuelCount: Promise<any[]>;
  flightRow: Promise<any[]>;
  routeRow: Promise<any[]>;
  flightlistParams: Promise<parameterStruct[]>;
}

export class fuelCalcReport {
  usedFinalreserve: number = 15 * 60;
  usedTaxiDuration: number = 1 * 60;
  usedContigencyFactor: number = 0.1;
  usedAdditionalFuel: number = 0;
  additionalFuelRemarks: string;
  finalReserveRmks: string;

  usedExtraFuel: number = 0;
  usedBallastFuel: number = 0;

  /**
   * The error that aborted the last `initFuel` run, or `undefined` if it
   * succeeded. Mirrors `mAndBandPerformance.lastError` — same reason: the catch
   * below rolls back and the only other trace is a `console.log`, which on iOS
   * reaches nobody (the JS console bridge was removed for perf).
   */
  lastError: unknown = undefined;

  async initFuel(
    flightId: string,
    classCom: any,
    acft: aircraftStruct,
    routeDist: number,
    alternateDist: number,
    fuelOnBoard: number,
    checksum: string,
    opsMissionType: opsMissionTypeEnum,
    navlog: navLegStruct[],
    started?: StartedFuelReads,
    writeEndurance = false
  ): Promise<boolean> {
    // These rows do not change during the calculation, so they are read now
    // and awaited where they are used. They were three serial requests.
    const startRead = (name: string): Promise<any[]> => {
      const rows: Promise<any[]> = classCom.namedQuery(name, { flightId });
      // Awaited below. This only stops a throw before that leaving it unhandled.
      rows.catch(() => undefined);
      return rows;
    };
    const fuelCount = started?.fuelCount ?? startRead("fuelCountByFlightId");
    const flightRow = started?.flightRow ?? startRead("flightlistByFlightId");
    const routeRow = started?.routeRow ?? startRead("navRouteDataByFlightId");

    const flightlistParams = await (started?.flightlistParams ??
      getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all));

    this.lastError = undefined;
    try {
      // first et fuel on board

      const uid = classCom.package_md5(flightId.toString() + "|" + opsMissionTypeEnum.all).substring(0, 20);
      await addParameter(
        uid,
        "fuel",
        property.actFuelOnBoard,
        opsMissionTypeEnum.all,
        fuelOnBoard.toString(),
        "",
        "",
        "",
        "",
        true
      );

      const estTimeDep: Date = new Date(
        Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
      );

      const cfvSpecific: opsAocRulesProvider = new opsAocRulesProvider();
      const cfvFinalReserve: rmksStruct = cfvSpecific.getFinalReserveTime(estTimeDep, navlog);
      this.usedFinalreserve = cfvFinalReserve.value;
      this.finalReserveRmks = cfvFinalReserve.rmk;

      const CfvadditionalFuel = await cfvSpecific.getAdditionalFuel(estTimeDep, navlog, acft, classCom);

      this.usedAdditionalFuel = CfvadditionalFuel.value;
      this.additionalFuelRemarks = CfvadditionalFuel.rmk;
      this.finalReserveRmks = cfvFinalReserve.rmk;

      const fuelOnBoardVol: number = fuelOnBoard / acft.fuelDensity;

      // tripfuel
      const tripFuel: number = (routeDist / acft.tacticalAirspeed) * acft.tacticalFuelBurn;
      const alternateFuel: number = (alternateDist / acft.tacticalAirspeed) * acft.tacticalFuelBurn;
      const taxiFuel: number = acft.tacticalFuelBurn * this.usedTaxiDuration;
      const contingencyFuel: number = tripFuel * this.usedContigencyFactor;
      const additionalFuel: number = this.usedAdditionalFuel;
      const ballastFuel: number = this.usedBallastFuel;

      // fuel expected to be burned
      const fuelBurnOff: number = tripFuel + 2 * taxiFuel;

      // calculated values
      // ----------------- //

      const takeOffFuel: number = fuelOnBoard - taxiFuel;
      const landingFuel: number = fuelOnBoard - tripFuel - taxiFuel;

      const finalReserveFuel: number = this.usedFinalreserve * acft.tacticalFuelBurn;
      const reserveFuel: number = alternateFuel + finalReserveFuel;
      const minTakeOffFuelReq: number = tripFuel + reserveFuel + 1 * taxiFuel + additionalFuel;
      const minRampfuelReq = taxiFuel + tripFuel + reserveFuel + additionalFuel + contingencyFuel;
      const extraFuel: number = fuelOnBoard - minRampfuelReq;
      const newTimestamp = new Date().getTime();

      // The figures stay in memory and go into the calculation result. They
      // are not written as parameter rows.
      const figures = new CalcFigures();
      figures.set(property.tripFuel, tripFuel.toString());
      figures.set(property.alternateFuel, alternateFuel.toString());
      // await addParameter(uid, "fuel", property.holdingFuel, opsMissionType, holdingFuel.toString(), "", "", "", "", true)
      figures.set(property.taxiFuel, taxiFuel.toString());
      figures.set(property.contingencyFuel, contingencyFuel.toString());
      figures.set(property.additionalFuel, additionalFuel.toString());
      figures.set(property.ballastFuel, ballastFuel.toString());
      figures.set(property.extraFuel, extraFuel.toString());
      figures.set(property.finalReserve, finalReserveFuel.toString());
      figures.set(property.reserveFuel, reserveFuel.toString());
      figures.set(property.minTakeOffFuelReq, minTakeOffFuelReq.toString());
      figures.set(property.minRampFuelReq, minRampfuelReq.toString());
      figures.set(property.estLandingFuel, landingFuel.toString());

      const annunciators: string = Math.round(extraFuel) + " KG EXTRA FUEL;";

      let processResultState: processResultStateEnum = processResultStateEnum.ok;

      if (extraFuel < 0) {
        processResultState = processResultStateEnum.fail;
      }

      // Monotonic revision counter: under upsert-in-place a row count collapses to
      // 1, so the displayed revision is read from the `revision` column instead.
      // Stamp max(revision)+1 once and use the same value for the header + the row.
      const nextRevision = Number((await fuelCount)[0].n) + 1;

      // Render the markdown BEFORE the single upsert so one write carries the
      // final processReport. Collapses the old insert("being created") +
      // fuelUpdateProcessReport patch pair (a same-ms patch would LWW-reject on
      // the server) and the obsolete pre-step — ON CONFLICT(rowId) upserts in place.
      const fuelParams = figures.rows;
      const processReport = await this.createFuelCalculation(
        flightId,
        classCom,
        acft,
        opsMissionType,
        uid.toString(),
        fuelOnBoard,
        nextRevision,
        flightRow,
        routeRow,
        fuelParams
      );
      // The fuel row joins the parameter flush: one request instead of two.
      // The server applies them in queue order, so the parameters still land
      // first, and the row settles from its own verdict.
      // The calculation result: this run's fuel figures as one document.
      const resultJson = buildCalcResult({ inputs: { fuelOnBoard }, values: valuesFromParams(fuelParams) });
      const rowId = rowIdFor([flightId, opsMissionType, newTimestamp]);
      const signatureId = await currentSignatureId(flightId, classCom);
      // The endurance of the flight plan leaves with the same flush. A newer stored value wins.
      const endurance = writeEndurance ? this.getEnduranceHHmm(acft, fuelOnBoard) : undefined;
      const enduranceWritten =
        endurance === undefined
          ? Promise.resolve()
          : queueParameter(
              flightId,
              "flightlist",
              property.fplEndurance,
              opsMissionTypeEnum.all,
              endurance,
              "",
              "",
              "",
              ""
            ).catch((err) => {
              if (!isBenignLwwReject(err)) throw err;
            });
      enduranceWritten.catch(() => undefined);
      const inserted = queueCommand({
        name: "fuelInsert",
        params: {
          rowId,
          Timestamp: newTimestamp,
          flightId,
          signatureId,
          resultJson,
          parameterHash: uid.toString(),
          // The summary column is INTEGER; calculation parameters retain the
          // fractional value. Preserve negative fuel deficits when rounding.
          fuelOnBoardMass: Math.round(fuelOnBoard),
          fuelOnBoardVol,
          checksum,
          opsMissionType,
          annunciators,
          processResultState,
          processReport,
          revision: nextRevision,
          updatedAt: Date.now(),
        },
      });
      // The flush can settle a reject before the await below reaches it.
      inserted.catch(() => undefined);
      await execAccuParameters();
      await inserted;
      await enduranceWritten;
      // Mass and balance asks for this row next. It is the latest live fuel
      // row of this mission, so the answer is known without a request.
      seedUserRead("fuelHashAndMassByFlight", { flightId, opsMissionType }, [
        { parameterHash: uid.toString(), fuelOnBoardMass: Math.round(fuelOnBoard) },
      ]);
      seedUserRead("fuelResultByFlight", { flightId, opsMissionType }, [
        { rowId, resultJson, signatureId, processResultState },
      ]);
      return true;
    } catch (err) {
      // A 409 carrying reason 'lww' is a benign no-op, not a write fault: the
      // app-server already holds an equal-or-newer row for this
      // (flightId, opsMissionType). The fuelInsert wrote nothing, but nothing
      // was lost either — treat it as success and do NOT run the destructive
      // rollback, which would wipe the already-committed row and its params
      // on a benign collision (e.g. a concurrent same-row recalc). The
      // browser DB service throws HttpError with a numeric `status`; native
      // never produces a 409 (it writes local SQLite directly).
      //
      // Discriminate on the body, NOT on the status alone. dispatch.ts maps
      // every CLASSIFIED reject to 409 — 'not-found', 'immutable' and the
      // per-op SAVEPOINT bind/constraint message land here too, and each of
      // those means the row was REFUSED. Swallowing them returned `true` from
      // a write that never happened, which defeated the create-flight
      // `fuelOk` gate on exactly the fault it was added to catch.
      if (isBenignLwwReject(err)) {
        console.log("fuelInsert LWW no-op (409) — server holds equal/newer row " + flightId);
        return true;
      }
      // Rollback on a genuine write fault: soft-obsolete EVERY live version of
      // this flight+mission, not just the row this run wrote.
      //
      // The scope is the point. Before `6c961b06` the append-only writers
      // obsoleted this same (flightId, opsMissionType) set on every write and
      // hard-deleted it on failure — either way nothing was live after a failed
      // calculation, which is the invariant `initPerformance`'s liveness gate
      // still depends on. `6c961b06` collapsed the table to one row per key, so
      // a single rowId WAS the whole set; restoring `Timestamp` to the key
      // silently narrowed this line back to one version and left the PREVIOUS
      // one live and current — next to parameters this failed run had already
      // half-overwritten (it aborts at `getTripFuel`, after some were written).
      //
      // Obsoleting beats the old hard delete: the history survives as
      // obsolete = 1 instead of being destroyed.
      //
      // AWAITED. Fire-and-forget returned `false` to the caller while the UPDATE
      // was still in flight, so the next step's liveness gate could still read
      // the row this rollback is retracting — and the widened scope enumerates
      // rowIds for the sync broadcast first, which makes that window longer.
      //
      // FUEL ONLY. This catch retracts the row THIS method wrote and nothing
      // else — retracting `loading`/`perf` from here was tried on 2026-08-14 and
      // reverted the same day.
      //
      // The reason is not the order any particular caller runs its steps in.
      // It is that THIS METHOD CANNOT KNOW WHETHER ITS CALLER HAS DOWNSTREAM
      // STEPS AT ALL. `initFuel` is reached from a chain (`updateOfpCalculations`,
      // the ahead-flight recycle in `ops-control.service.ts`, and create-flight)
      // and also on its own, from the `case "initFuel"` dispatch in `focMain`,
      // where there is no chain and `loading`/`perf` are whatever an EARLIER
      // pass computed. Retracting from here obsoletes that earlier pass's good
      // data on a caller that never asked for a chain. (The dispatch case does
      // NOT discard the boolean — `focMain` `case "initFuel"` reads it, logs
      // `lastError` on false and returns `success === true`. The rule stands
      // without that clause; it was removed because it was checkably false.)
      //
      // Retracting the downstream reports is therefore the CHAIN OWNER's job:
      // see `focMain.updateOfpCalculations`, which gates on each step and calls
      // `retractDownstreamCalculations`. The recycle is the second chain owner
      // and gates the same way, but needs no retraction — it opens by obsoleting
      // all three tables, so a step that never runs leaves its rows obsolete.
      //
      // (Historical note, because the earlier wording rested on it: the recycle
      // ran mass and balance → performance → fuel LAST until 2026-08-14. That
      // order could not work once every live row is obsoleted up front, because
      // mass and balance reads the fuel row, so it was reordered. The rule above
      // never depended on it.)
      try {
        await classCom.namedCommand("fuelObsoleteByFlightMission", { flightId, opsMissionType });
      } catch (e: unknown) {
        console.error("namedCommand fuelObsoleteByFlightMission: ", e);
      }
      console.log("flightlist was rolled back " + flightId + "! " + err);
      this.lastError = err;

      // NOTE this deletes nothing, and that is now deliberate. `initFuel`
      // writes every parameter under `uid` (= md5(flightId|all)[0:20], see the
      // top of this method), not under `flightId`, so this reconcile addresses
      // an empty scope. Do NOT "fix" it to pass `uid`: that hash is keyed
      // per-FLIGHT, not per-mission-type, so a failure on one mission type
      // would soft-delete a sibling mission type's good fuel parameters.
      //
      // Nothing needs it either. The obsoleted row above is the liveness
      // signal — `initPerformance` and the report reads all gate on a live row
      // (obsolete = 0), never on parameter presence — so orphaned params are
      // unreachable rather than dangerous. Kept as a no-op instead of deleted
      // so the intent stays visible. See ../TODO.md, "the rollback scopes".
      await deleteAllParameters(flightId, "fuel");

      // Falling off the end here resolved `undefined` from a declared
      // Promise<boolean>, so a rolled-back fuel calculation was indistinguishable
      // from a completed one at every caller — the same defect the W&B side had.
      // It is what let the create-flight `fuelOk` gate pass a fuel row that was
      // never committed, and the mass and balance failure that followed then
      // overwrote this cause in the operator's alert.
      return false;
    }
  }

  async getFuelReport(
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    classCom: any,
    returnMarkdown = false
  ): Promise<string> {
    const result = await classCom.namedQuery("fuelProcessReportByFlight", { flightId, opsMissionType });

    // No live row means initFuel never committed one — it rolls back and
    // swallows on any fault. Reporting that beats "Cannot read properties of
    // undefined (reading 'processReport')", which points at this reader rather
    // than at the calculation that failed. The same read backs getTaxiFuel &
    // co., so a missing row also takes the mass and balance down with it.
    if (result.length === 0) {
      throw new Error(
        `no live fuel calculation for flight ${flightId} (mission ${opsMissionTypeEnum[opsMissionType]}) — ` +
          "the calculation did not complete, or its row was superseded"
      );
    }

    const mdp = new markDownProcessing();

    if (returnMarkdown) {
      return result[0].processReport;
    }

    const html: string = await mdp.convMarkdown(
      result[0].processReport,
      classCom.appearance,
      flightId,
      opsMissionType,
      classCom,
      undefined,
      result[0].signatureId
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }

  async getProcessResultState(flightId: string, opsMissionType: opsMissionTypeEnum, classCom: any): Promise<string> {
    const result = await classCom.namedQuery("fuelProcessResultStateByFlight", { flightId, opsMissionType });

    if (result.length == 0) {
      return "NIL";
    }

    if (result[0].processResultState == 0) {
      return "N/A";
    }

    if (result[0].processResultState == 1) {
      return "OK";
    }

    if (result[0].processResultState == 2) {
      return "WARN";
    }

    if (result[0].processResultState == 3) {
      return "FAIL";
    }

    return "N/A";
  }

  async createFuelCalculation(
    flightId,
    classCom,
    acft: aircraftStruct,
    opsMissionType: opsMissionTypeEnum,
    uid: string,
    fuelOnBoard: number,
    revisionNo: number,
    flightRow: Promise<any[]>,
    routeRow: Promise<any[]>,
    givenFuelParams?: parameterStruct[]
  ): Promise<string> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    const FlightNumber = (await flightRow)[0].flightNumber;
    // Revision is the value being stamped on this recalc (max(revision)+1),
    // computed by the caller so the header and the persisted row stay in sync.
    const revision: string = String(revisionNo);
    const utcTimeNow = getUtcTime(new Date());
    const issueTime =
      padLeft(utcTimeNow.getHours().toString(), "0", 2) +
      ":" +
      padLeft(utcTimeNow.getMinutes().toString(), "0", 2) +
      " UTC";
    const estTimeDep: Date = new Date(
      Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
    );
    const estTimeDep_gui_LT =
      padLeft(estTimeDep.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep.getMinutes().toString(), "0", 2) +
      " LT";
    const estTimeDep_utc = getUtcTime(estTimeDep);
    const estTimeDep_gui_UTC =
      padLeft(estTimeDep_utc.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep_utc.getMinutes().toString(), "0", 2) +
      " UTC";

    const routeData = (await routeRow)[0];
    const ete: number = routeData.routeTimeFromOriginToDest;

    const originName: string = getParameterOfArray(
      flightId,
      "flightlist",
      property.icaoOrigin,
      flightlistParams
    )[0].value.trim();
    const destName: string = getParameterOfArray(
      flightId,
      "flightlist",
      property.destName,
      flightlistParams
    )[0].value.trim();
    const altName: string = getParameterOfArray(
      flightId,
      "flightlist",
      property.alternateName,
      flightlistParams
    )[0].value.trim();

    const origin: string = getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams)[0].value;
    const dest: string = getParameterOfArray(flightId, "flightlist", property.icaoDest, flightlistParams)[0].value;
    const alternate: string = getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams)[0]
      .value;

    const d1 = new Date();
    const estTimeArr = new Date(estTimeDep);
    estTimeArr.setSeconds(d1.getSeconds() + ete);

    const estTimeArr_gui_LT =
      padLeft(estTimeArr.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeArr.getMinutes().toString(), "0", 2) +
      " LT";
    const estTimeArr_gui_UTC =
      padLeft(estTimeArr.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeArr.getMinutes().toString(), "0", 2) +
      " UTC";

    // here come the fuel values

    // uid + fuelOnBoard are passed in from initFuel (the exact values the single
    // fuelInsert upsert will persist). They are NOT read back from the fuel row:
    // under the one-write model this render runs BEFORE the row is inserted, so a
    // read-back would throw on the first calc / web create-flight (empty result)
    // and go stale on re-calc.
    const fuelonBoard = Math.round(fuelOnBoard);

    // bulk-load all fuel params for this uid in one SQL call (was 10 round trips).
    const fuelParams = givenFuelParams ?? (await getParametersArray(uid, "fuel", opsMissionType));
    const readFuel = (p: property) =>
      Math.round(Number(getParameterOfArray(uid, "fuel", p, fuelParams)[0]?.value ?? 0));

    const tripFuel = readFuel(property.tripFuel);
    const alternateFuel = readFuel(property.alternateFuel);
    const taxiFuel = readFuel(property.taxiFuel);
    const contingencyFuel = readFuel(property.contingencyFuel);
    const additionalFuel = readFuel(property.additionalFuel);
    const extraFuel = readFuel(property.extraFuel);
    const finalReserve = readFuel(property.finalReserve);
    const reserveFuel = readFuel(property.reserveFuel);
    const minTakeOffFuelReq = readFuel(property.minTakeOffFuelReq);
    const minRampFuelReq = readFuel(property.minRampFuelReq);

    const mdText_lst: string[] = [];

    mdText_lst.push("## PLANNED FUEL / FUEL CALCULATION");
    mdText_lst.push(
      "Flight **{FlightNumber}** -  Ops Mission Type: **{opsMissionType}** - revision {revision} @  {issueTime} \n"
    );
    mdText_lst.push(
      "ETD **{estTimeDep_gui_UTC} / {estTimeDep_gui_LT}**:   **{originName}** to **{destName}**,  Alternate {altName}\n"
    );
    mdText_lst.push("________________________");

    mdText_lst.push("");
    mdText_lst.push(" ");
    mdText_lst.push("|           | REMARKS | FUEL MASS [KG] | TIME [HH:mm] |");
    mdText_lst.push("|:--------------|:--------|:---------:|:----:|");
    mdText_lst.push("| Startup + Taxi          |    | {taxiFuel}      | {taxiEndurance} |");
    mdText_lst.push("| Trip          |{origin} - {dest}    |{tripFuel} | {tripEndurance} |");
    mdText_lst.push("| Contingency    |   {contingencyFuelRmk}      | {contingencyFuel}| {contEndurance} |");
    mdText_lst.push("| Alternate     |{dest} - {alternate}    | {alternateFuel}        | {alternateEndurance} |");
    mdText_lst.push("| Final Reserve |  {finalReserveRmks}       | {finalReserve}        | {finalResEndurance} |");
    mdText_lst.push("| Additional    |  {addFuelRmk}       | {additionalFuel}        | {additionalFuelEndurance} |");
    mdText_lst.push("|               |         |           |      |");
    mdText_lst.push("| **Min req Ramp**|   |**{reqRampFuel}**   | **{minRampFuelEndurance}** |");
    mdText_lst.push("|               |         |           |      |");
    mdText_lst.push("| On Board   |         | {fuelOnBoard}      |  {enduranceHHmm} |");
    mdText_lst.push("| Extra         |         | {extraFuel}       |  {extraFuelEndurance} |");

    let mdString: string = "";
    for (let i = 0; i < mdText_lst.length; i++) {
      mdString += mdText_lst[i] + "\n";
    }
    const enduranceHHmm = this.getEnduranceHHmm(acft, fuelonBoard);
    const tripEndurance = this.getEnduranceHHmm(acft, tripFuel);
    const contEndurance = this.getEnduranceHHmm(acft, contingencyFuel);
    const taxiEndurance = this.getEnduranceHHmm(acft, taxiFuel);
    const alternateEndurance = this.getEnduranceHHmm(acft, alternateFuel);
    const finalResEndurance = this.getEnduranceHHmm(acft, finalReserve);
    const additionalFuelEndurance = this.getEnduranceHHmm(acft, additionalFuel);
    const minRampFuelEndurance = this.getEnduranceHHmm(acft, minTakeOffFuelReq);
    const extraFuelEndurance = this.getEnduranceHHmm(acft, extraFuel);

    // special request by Harald Blank
    const expectedLandingFuel: string = Math.round(fuelonBoard - taxiFuel - tripFuel).toString();

    mdString = mdString.replace("{enduranceHHmm}", enduranceHHmm);
    mdString = mdString.replace("{enduranceHHmm}", enduranceHHmm);
    mdString = mdString.replace("{tripEndurance}", tripEndurance);
    mdString = mdString.replace("{taxiEndurance}", taxiEndurance);
    mdString = mdString.replace("{contEndurance}", contEndurance);
    mdString = mdString.replace("{alternateEndurance}", alternateEndurance);
    mdString = mdString.replace("{finalResEndurance}", finalResEndurance);
    mdString = mdString.replace("{additionalFuelEndurance}", additionalFuelEndurance);
    mdString = mdString.replace("{extraFuelEndurance}", extraFuelEndurance);
    mdString = mdString.replace("{expectedLandingFuel}", expectedLandingFuel);
    mdString = mdString.replace("{minRampFuelEndurance}", minRampFuelEndurance);

    mdString = mdString.replace("{origin}", origin);
    mdString = mdString.replace("{origin}", origin);
    mdString = mdString.replace("{dest}", dest);
    mdString = mdString.replace("{dest}", dest);
    mdString = mdString.replace("{dest}", dest);
    mdString = mdString.replace("{alternate}", alternate);
    mdString = mdString.replace("{FlightNumber}", FlightNumber);
    mdString = mdString.replace("{revision}", revision);
    mdString = mdString.replace("{issueTime}", issueTime);
    mdString = mdString.replace("{destName}", destName);
    mdString = mdString.replace("{altName}", altName);
    mdString = mdString.replace("{originName}", originName);

    mdString = mdString.replace("{addFuelRmk}", this.additionalFuelRemarks);
    mdString = mdString.replace("{finalReserveRmks}", this.finalReserveRmks);
    mdString = mdString.replace("{contingencyFuelRmk}", Math.round(this.usedContigencyFactor * 100) + "% Tripfuel");

    mdString = mdString.replace("{estTimeDep_gui_UTC}", estTimeDep_gui_UTC);
    mdString = mdString.replace("{estTimeDep_gui_LT}", estTimeDep_gui_LT);
    mdString = mdString.replace("{estTimeArr_gui_UTC}", estTimeArr_gui_UTC);
    mdString = mdString.replace("{estTimeArr_gui_LT}", estTimeArr_gui_LT);

    mdString = mdString.replace("{tripFuel}", tripFuel.toString());
    mdString = mdString.replace("{reserveFuel}", reserveFuel.toString());
    mdString = mdString.replace("{extraFuel}", extraFuel.toString());
    mdString = mdString.replace("{endurance}", enduranceHHmm.toString());
    mdString = mdString.replace("{tripFuel}", tripFuel.toString());
    mdString = mdString.replace("{contingencyFuel}", contingencyFuel.toString());
    mdString = mdString.replace("{alternateFuel}", alternateFuel.toString());
    mdString = mdString.replace("{finalReserve}", finalReserve.toString());
    mdString = mdString.replace("{additionalFuel}", additionalFuel.toString());
    mdString = mdString.replace("{takeOffFuel}", minTakeOffFuelReq.toString());
    mdString = mdString.replace("{fuelOnBoard}", fuelonBoard.toString());
    mdString = mdString.replace("{fuelOnBoard}", fuelonBoard.toString());
    mdString = mdString.replace("{extraFuel}", extraFuel.toString());
    mdString = mdString.replace("{taxiFuel}", taxiFuel.toString());
    mdString = mdString.replace("{reqRampFuel}", minRampFuelReq.toString());
    mdString = mdString.replace("{reqRampFuel}", minRampFuelReq.toString());
    mdString = mdString.replace(
      "{pic}",
      getParameterOfArray(flightId, "flightlist", property.pilotInCommand, flightlistParams)[0].value
    );
    mdString = mdString.replace("{opsMissionType}", opsMissionTypeEnum[opsMissionType].replace("_", " ").toUpperCase());

    return alignMarkdownTablesInDocument(mdString);
  }

  getEnduranceHHmm(acft: aircraftStruct, fuelBase: number): string {
    if (fuelBase < 0) {
      return "INSUFF FUEL";
    }
    const endurance: number = fuelBase / acft.tacticalFuelBurn;
    const endurance_h: number = Math.floor(endurance / 3600);
    const endurance_min: number = Math.round((endurance - endurance_h * 3600) / 60);

    return padLeft(endurance_h.toString(), "0", 2) + ":" + padLeft(endurance_min.toString(), "0", 2);
  }

  /**
   * Parameter hash of the live fuel row, or a named error if there is none.
   *
   * The three readers below used to index `[0]` straight off the query, so a
   * flight whose fuel calculation never committed failed with "Cannot read
   * properties of undefined (reading 'parameterHash')" — deep inside
   * initMassAndBalance, which then rolled the W&B back and left the operator
   * looking at a missing loading report. Name the actual gap instead.
   */
  /** The live fuel version of this flight and mission, or null when none is readable. */
  liveFuel(classCom: any, opsMissionType: opsMissionTypeEnum, flightId: string): Promise<LiveCalc | null> {
    return liveCalc(classCom, "fuel", flightId, opsMissionType, getParametersArray);
  }

  private async fuelValue(
    classCom: any,
    opsMissionType: opsMissionTypeEnum,
    flightId: string,
    parameter: property
  ): Promise<number> {
    const live = await this.liveFuel(classCom, opsMissionType, flightId);
    const value = live?.values[parameter];

    if (value === undefined) {
      throw new Error(
        `no live fuel calculation for flight ${flightId} (mission ${opsMissionTypeEnum[opsMissionType]}) — ` +
          "mass and balance needs it; the fuel calculation did not complete, or its row was superseded"
      );
    }

    return Number(value);
  }

  getTaxiFuel(classCom: any, opsMissionType: opsMissionTypeEnum, flightId: string): Promise<number> {
    return this.fuelValue(classCom, opsMissionType, flightId, property.taxiFuel);
  }
  getTripFuel(classCom: any, opsMissionType: opsMissionTypeEnum, flightId: string): Promise<number> {
    return this.fuelValue(classCom, opsMissionType, flightId, property.tripFuel);
  }
  getEstLandingFuel(classCom: any, opsMissionType: opsMissionTypeEnum, flightId: string): Promise<number> {
    return this.fuelValue(classCom, opsMissionType, flightId, property.estLandingFuel);
  }
}
